import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import DashboardHeader from "../components/DashboardHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { HandCoins, Loader2, User, Wallet, Package, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function Fiados() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [paySaving, setPaySaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fiado/customers");
      setCustomers(data || []);
    } finally { setLoading(false); }
  };

  const openDetail = async (name) => {
    try {
      const { data } = await api.get(`/fiado/customers/${encodeURIComponent(name)}`);
      setDetail(data);
      setDetailOpen(true);
    } catch { toast.error("Erro ao carregar detalhe"); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const registerPayment = async (e) => {
    e.preventDefault();
    if (!detail) return;
    const amount = Number(payAmount);
    if (!(amount > 0)) { toast.error("Valor inválido"); return; }
    if (amount > detail.total_due + 0.001) { toast.error("Valor superior ao total em dívida"); return; }
    setPaySaving(true);
    try {
      await api.post("/fiado/payments", { customer_name: detail.customer_name, amount });
      toast.success("Pagamento registado");
      setPayOpen(false);
      setPayAmount("");
      // reload
      const { data } = await api.get(`/fiado/customers/${encodeURIComponent(detail.customer_name)}`);
      setDetail(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registar pagamento");
    } finally { setPaySaving(false); }
  };

  const totalOpen = customers.reduce((a, c) => a + c.total_due, 0);
  const openCustomers = customers.filter((c) => !c.is_settled);
  const overdueCount = customers.filter((c) => !c.is_settled && (c.oldest_open_days || 0) > 40).length;

  return (
    <div className="min-h-screen bg-background" data-testid="fiados-page">
      <DashboardHeader title="Fiados" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="lb-fade-in">
          <div className="lb-overline">Fiados</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Clientes em dívida</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {openCustomers.length} em dívida · Total: <span className="font-semibold text-[hsl(var(--destructive))]">{format(totalOpen)}</span>
            {overdueCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] text-xs font-semibold">
                <AlertTriangle className="w-3 h-3" /> {overdueCount} com +40 dias
              </span>
            )}
          </p>
        </div>

        {loading ? (
          <div className="space-y-3">{Array.from({length:3}).map((_,i)=><div key={i} className="h-20 bg-white rounded-2xl lb-shadow-card animate-pulse" />)}</div>
        ) : customers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl lb-shadow-card" data-testid="empty-fiados">
            <HandCoins className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <h3 className="mt-3 font-display text-xl">Nenhum fiado em aberto</h3>
            <p className="text-sm text-muted-foreground mt-1">Ótimo! Todas as contas estão saldadas.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {customers.map((c) => {
              const isOverdue = !c.is_settled && (c.oldest_open_days || 0) > 40;
              return (
                <button
                  key={c.customer_name}
                  onClick={() => openDetail(c.customer_name)}
                  className={`w-full text-left bg-white rounded-2xl lb-shadow-card p-4 lb-hover-lift flex items-center gap-4 ${isOverdue ? "ring-1 ring-[hsl(var(--destructive))]/40" : ""} ${c.is_settled ? "opacity-70" : ""}`}
                  data-testid={`fiado-customer-${c.customer_name}`}
                >
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${c.is_settled ? "bg-[hsl(var(--primary))]/10" : "bg-[hsl(var(--secondary))]"}`}>
                    {c.is_settled ? (
                      <CheckCircle2 className="w-5 h-5 text-[hsl(var(--primary))]" />
                    ) : (
                      <User className="w-5 h-5 text-[hsl(var(--primary))]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate flex items-center gap-2">
                      {c.customer_name}
                      {c.is_settled && <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">Saldo liquidado</span>}
                      {isOverdue && <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]">+40 dias</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                      <div>
                        Liquidado: <span className="font-semibold text-[hsl(var(--primary))]">{format(c.total_paid || 0)}</span>
                        {" · "}
                        Em dívida: <span className={`font-semibold ${c.is_settled ? "text-muted-foreground" : "text-[hsl(var(--destructive))]"}`}>{format(c.total_due || 0)}</span>
                      </div>
                      <div>
                        {c.sales_count} venda(s) · última: {new Date(c.last_sale_at).toLocaleDateString("pt-PT")}
                        {!c.is_settled && c.oldest_open_days > 0 && ` · dívida há ${c.oldest_open_days} dia(s)`}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-display text-xl ${c.is_settled ? "text-muted-foreground line-through" : "text-[hsl(var(--destructive))]"}`}>
                      {format(c.total_due || 0)}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.is_settled ? "saldado" : "em dívida"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>

      {/* Customer detail */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fiado-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{detail.customer_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-lg bg-secondary p-4 flex items-center justify-between">
                  <div>
                    <div className="lb-overline">Em dívida</div>
                    <div className="font-display text-3xl text-[hsl(var(--destructive))]">{format(detail.total_due)}</div>
                  </div>
                  <Button
                    onClick={() => { setPayAmount(String(detail.total_due)); setPayOpen(true); }}
                    disabled={detail.total_due <= 0}
                    className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white"
                    data-testid="register-payment-btn"
                  >
                    <Wallet className="w-4 h-4 mr-2" /> Registar pagamento
                  </Button>
                </div>

                <div>
                  <div className="lb-overline mb-2">Vendas ({detail.sales.length})</div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {detail.sales.map((s) => (
                      <div key={s.id} className="text-sm border border-border rounded-lg p-2.5 flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="truncate">{s.product_name} · {s.variant_name}</div>
                          <div className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString("pt-PT")} · {s.payment_type}</div>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <div className="text-xs">{format(s.sale_price * s.quantity)}</div>
                          <div className={`text-xs font-semibold ${s.amount_due > 0 ? "text-[hsl(var(--destructive))]" : "text-[hsl(var(--primary))]"}`}>
                            {s.amount_due > 0 ? `Dívida ${format(s.amount_due)}` : "Pago"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {detail.payments.length > 0 && (
                  <div>
                    <div className="lb-overline mb-2">Pagamentos registados</div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {detail.payments.map((p) => (
                        <div key={p.id} className="text-sm flex justify-between border-b border-border py-1.5">
                          <span className="text-muted-foreground">{new Date(p.created_at).toLocaleDateString("pt-PT")}</span>
                          <span className="font-semibold text-[hsl(var(--primary))]">{format(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Register payment */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Registar pagamento</DialogTitle>
          </DialogHeader>
          <form onSubmit={registerPayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Valor recebido (CVE)</Label>
              <Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} autoFocus data-testid="payment-amount-input" />
              <p className="text-xs text-muted-foreground">Em dívida agora: {format(detail?.total_due || 0)}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPayOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={paySaving} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58]" data-testid="confirm-payment-btn">
                {paySaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirmar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
